import { handleApiError, type ErrorHandlerOptions } from './error-handler'

const API_BASE_URL = import.meta.env.VITE_API_URL;

// Genel veri türleri
export interface DocsetInfo {
  id: string;
  name: string;
  description?: string;
}

export interface ChatRequestData {
  messages: { role: string; content: string }[];
  provider: string;
  docset_id: string;
  user_id?: string;
  session_id?: string;
}

interface ApiClientConfig {
  baseUrl?: string
  defaultHeaders?: Record<string, string>
}

interface RequestOptions extends RequestInit {
  errorHandler?: ErrorHandlerOptions
  retryCount?: number
  retryDelay?: number
}

function getTokenData(provider?: string): { token: string; expiry: number } | null {
  if (!provider) return null
  const key = `encrypted_llm_token_${provider}`
  const data = localStorage.getItem(key)
  if (!data) return null
  
  try {
    const parsed = JSON.parse(data)
    if (!parsed.token || !parsed.expiry) return null
    if (Date.now() > parsed.expiry) {
      localStorage.removeItem(key)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function clearToken(provider?: string): void {
  if (!provider) return
  const key = `encrypted_llm_token_${provider}`
  localStorage.removeItem(key)
}


async function withRetry<T>(
  fn: () => Promise<T>, 
  retryCount: number = 3,
  retryDelay: number = 1000
): Promise<T> {
  for (let i = 0; i < retryCount; i++) {
    try {
      return await fn()
    } catch (error) {
      if (i === retryCount - 1) throw error
      
      const delay = retryDelay * Math.pow(2, i)
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
  throw new Error('Max retries exceeded')
}

class ApiClient {
  private config: Required<ApiClientConfig>

  constructor(config: ApiClientConfig = {}) {
    // Session ID'yi header'lara ekle
    const sessionId = typeof window !== 'undefined' ? sessionStorage.getItem('rag_session_id') : null;
    
    this.config = {
      baseUrl: config.baseUrl || API_BASE_URL,
      defaultHeaders: {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-ID': sessionId } : {}),
        ...config.defaultHeaders
      }
    }
  }

  private async makeRequest<T>(
    endpoint: string, 
    options: RequestOptions = {}
  ): Promise<T> {
    const { 
      errorHandler = {}, 
      retryCount = 0,
      retryDelay = 1000,
      ...fetchOptions 
    } = options

    const url = endpoint.startsWith('http') ? endpoint : `${this.config.baseUrl}${endpoint}`
    
    // Her istekte güncel session ID'yi alalım
    const sessionId = typeof window !== 'undefined' ? sessionStorage.getItem('rag_session_id') : null;
    
    // Session ID log edelim
    if (sessionId) {
      console.log(`API isteği için session ID: ${sessionId}`);
    } else {
      console.warn('API isteği için session ID bulunamadı!');
    }
    
    const headers = {
      ...this.config.defaultHeaders,
      ...(sessionId ? { 'X-Session-ID': sessionId } : {}),
      ...fetchOptions.headers
    }

    const requestConfig: RequestInit = {
      ...fetchOptions,
      credentials: 'include',
      headers
    }

    const executeRequest = async (): Promise<T> => {
      const response = await fetch(url, requestConfig)
      
      if (!response.ok) {
        const enhancedErrorHandler: ErrorHandlerOptions = {
          ...errorHandler,
          onTokenRefresh: async () => {
            const headerObj = headers as Record<string, string>
            const authHeader = headerObj['x-llm-token']
            if (authHeader) {
              Object.keys(localStorage)
                .filter(key => key.startsWith('encrypted_llm_token_'))
                .forEach(key => localStorage.removeItem(key))
            }
            
            if (errorHandler.onTokenRefresh) {
              await errorHandler.onTokenRefresh()
            }
          },
          onRetry: errorHandler.onRetry || (() => {
            return this.makeRequest(endpoint, { ...options, retryCount: 0 })
          })
        }

        await handleApiError(response, enhancedErrorHandler)
      }

      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        return response.json()
      }
      
      return response.text() as T
    }

    if (retryCount > 0) {
      return withRetry(executeRequest, retryCount, retryDelay)
    }
    
    return executeRequest()
  }

  async get<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.makeRequest<T>(endpoint, { 
      ...options, 
      method: 'GET' 
    })
  }

  async post<T, D = Record<string, unknown>>(
    endpoint: string, 
    data?: D, 
    options: RequestOptions = {}
  ): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    })
  }

  async put<T, D = Record<string, unknown>>(
    endpoint: string, 
    data?: D, 
    options: RequestOptions = {}
  ): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    })
  }

  async delete<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.makeRequest<T>(endpoint, { 
      ...options, 
      method: 'DELETE' 
    })
  }

  async upload<T>(endpoint: string, file: File, options: RequestOptions = {}): Promise<T> {
    const url = endpoint.startsWith('http') ? endpoint : `${this.config.baseUrl}${endpoint}`;
    const formData = new FormData();
    formData.append('file', file);
  
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {}),
    };
  
    const requestConfig: RequestInit = {
      ...options,
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers,
    };
    
    // Content-Type header'ı FormData için boş bırakılmalıdır
    const headersObj = requestConfig.headers as Record<string, string>;
    delete headersObj['Content-Type'];
  
    const response = await fetch(url, requestConfig);
    if (!response.ok) {
      await handleApiError(response, options.errorHandler);
    }
    return response.json();
  }

  async uploadMultiple<T>(endpoint: string, formData: FormData, options: RequestOptions = {}): Promise<T> {
    const url = endpoint.startsWith('http') ? endpoint : `${this.config.baseUrl}${endpoint}`;
    
    // For FormData, the browser sets the Content-Type header with the correct boundary.
    // We must remove our default 'application/json' header.
    const headers = { ...this.config.defaultHeaders, ...options.headers as Record<string, string> };
    delete headers['Content-Type'];
    
    const sessionId = typeof window !== 'undefined' ? sessionStorage.getItem('rag_session_id') : null;
    if (sessionId) {
      headers['X-Session-ID'] = sessionId;
    }

    const requestConfig: RequestInit = {
      ...options,
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers,
    };

    const response = await fetch(url, requestConfig);
    
    // Handle both success (201) and multi-status (207) responses
    if (!response.ok && response.status !== 207) {
      await handleApiError(response, options.errorHandler);
    }
    
    return response.json();
  }

  withAuth(provider?: string): ApiClient {
    const tokenData = getTokenData(provider)
    const headers: Record<string, string> = tokenData?.token 
      ? { 'x-llm-token': tokenData.token }
      : {}

    return new ApiClient({
      ...this.config,
      defaultHeaders: {
        ...this.config.defaultHeaders,
        ...headers
      }
    })
  }

  withHeaders(headers: Record<string, string>): ApiClient {
    return new ApiClient({
      ...this.config,
      defaultHeaders: {
        ...this.config.defaultHeaders,
        ...headers
      }
    })
  }
}

export const apiClient = new ApiClient()

export { ApiClient }

export const api = {
  docsets: {
    list: () => apiClient.get<{ docsets: DocsetInfo[] }>('/api/docsets'),
  },
  
  docs: {
    listAll: () => apiClient.get<Array<{
      id: string;
      docset_id: string;
      source: string;
      content: string;
      last_updated_at: string;
    }>>('/api/documents/'),
    
    listByDocset: (docsetId: string) => apiClient.get<Array<{
      id: string;
      source: string;
      content: string;
      last_updated_at: string;
    }>>(`/api/public/documents/${docsetId}`),
    
    get: async (id: string, docsetId: string): Promise<string> => {
      const documents = await apiClient.get<Array<{
        id: string;
        source: string;
        content: string;
        last_updated_at: string;
      }>>(`/api/public/documents/${docsetId}`);
      
      const document = documents.find(doc => doc.id === id);
      if (!document) {
        throw new Error('Document not found');
      }
      
      return document.content;
    },
  },
  
  llm: (provider?: string) => ({
    chat: (data: ChatRequestData) => apiClient.withAuth(provider).post('/api/chat', data),
    stream: (data: ChatRequestData) => apiClient.withAuth(provider).post('/api/chat/stream', data),
  }),
}

export { showSuccess, showInfo, showWarning } from './error-handler'
